import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { faLock, faUser } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import { PATH_MAP } from "../../configuration/PathMap"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { useMessage } from "../../context/useMessageContext"
import { useResellerLogged } from "../../context/useResellerContext"
import { useResellerAuth } from "../../hooks/Reseller/useResellerAuth"
import { useGetResellerProfile } from "../../hooks/Reseller/useGetResellerProfile"
import { useResetPassword } from "../../hooks/Components/useResetPassword"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import InputField from "../../components/InputField/InputField"
import Navbar from "../../components/LandPage/Navbar"
import logo from "../../assets/images/Logo2.png"

export const ResellerLoginPage: React.FC = () => {
  const { t } = useTranslation("reseller")

  const [username, setUsername] = useState("")

  const [password, setPassword] = useState("")

  const navigate = useNavigate()

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const { setMessage } = useMessage()

  const { setResellerProfile } = useResellerLogged()

  const { handleSignInReseller } = useResellerAuth()

  const { handleGetResellerProfile } = useGetResellerProfile()

  const { HandleRecoverPassword } = useResetPassword()

  const handleForgotPassword = async () => {
    if (!username.trim()) {
      setError(t("login.error_title"), t("login.recover_password_missing_email"))

      return
    }

    await HandleRecoverPassword(
      username,
      () => setMessage(t("login.recover_password_title"), t("login.recover_password_description")),
      API_ROUTES.AUTH.RESELLER_SEND_RECOVER_LINK,
    )
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    enableLoad()

    try {
      await handleSignInReseller({ username, password })

      await handleGetResellerProfile(setResellerProfile)

      navigate(PATH_MAP.RESELLER_DASHBOARD_PAGE, { replace: true })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("login.error_title")

      setError(t("login.error_title"), message)
    } finally {
      disableLoad()
    }
  }

  return (
    <Animate type={AnimationTypes.slideDownIn} duration="0.5s">
      <div className="min-h-screen flex flex-col items-center justify-center relative bg-white overflow-hidden">
        <Navbar showFunctions={false} showWhatsAppFloat={false} />

        <div className="w-full max-w-md p-8 md:p-6 mt-10">
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
            <div className="mb-6 flex flex-col items-center text-center">
              <img src={logo} alt="Logo Catalojei" className="mb-4 w-52 drop-shadow-xl md:w-64" />

              <h1 className="text-2xl font-bold text-gray-700 md:text-2xl">{t("login.title")}</h1>

              <p className="mt-2 max-w-sm text-sm leading-relaxed font-bold text-gray-500">{t("login.subtitle")}</p>
            </div>
          </Animate>

          <Animate type={AnimationTypes.slideUpIn} duration="1.8s">
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <InputField
                  icon={faUser}
                  value={username}
                  type="text"
                  placeholder={t("login.label_username_placeholder")}
                  animationTypes={AnimationTypes.slideUpIn}
                  onChange={(e) => setUsername(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.reseller.login.input.username"
                />

                <InputField
                  icon={faLock}
                  value={password}
                  type="password"
                  placeholder={t("login.label_password_placeholder")}
                  animationTypes={AnimationTypes.slideUpIn}
                  onChange={(e) => setPassword(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.reseller.login.input.password"
                />
              </div>

              <button
                type="submit"
                className="mt-6 w-full rounded-md bg-gradient-to-r from-orange-500 to-red-500 px-4 py-3 text-sm font-bold text-white shadow-lg transition-all duration-200 hover:scale-[1.01] hover:from-orange-400 hover:to-red-400"
                data-interaction-description-key="interactionDescriptions:pages.reseller.login.button.submit"
              >
                {t("login.button_submit")}
              </button>

              <button
                type="button"
                className="mt-4 w-full text-center text-sm font-semibold text-gray-500 hover:text-gray-700"
                onClick={handleForgotPassword}
                data-interaction-description-key="interactionDescriptions:pages.reseller.login.button.forgotPassword"
              >
                {t("login.forgot_password_link")}
              </button>

              <button
                type="button"
                className="mt-2 w-full text-center text-sm font-semibold text-gray-500 hover:text-gray-700"
                onClick={() => navigate(PATH_MAP.RESELLER_SIGNUP_PAGE)}
                data-interaction-description-key="interactionDescriptions:pages.reseller.login.button.signupLink"
              >
                {t("login.signup_link")}
              </button>
            </form>
          </Animate>
        </div>
      </div>
    </Animate>
  )
}
